import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
interface Notification {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
}
const NAMES = ['Carlos M.', 'Ana R.', 'Roberto G.', 'María L.', 'José P.', 'Laura S.', 'Fernando T.', 'Patricia V.', 'Diego H.', 'Sofía C.', 'Andrés B.', 'Valentina D.', 'Miguel Á.', 'Camila F.', 'Ricardo N.', 'Isabella Q.', 'Alejandro W.', 'Daniela J.', 'Javier K.', 'Gabriela E.', 'Luis O.', 'Natalia Z.', 'Sergio U.', 'Mariana I.', 'Eduardo A.', 'Paula R.', 'Héctor M.', 'Claudia T.', 'Raúl V.', 'Elena B.', 'Tomás G.', 'Lucía P.', 'Óscar D.', 'Carmen S.', 'Martín H.', 'Adriana L.'];
const CITIES = ['Miami', 'Houston', 'Los Angeles', 'New York', 'Chicago', 'San Antonio', 'Phoenix', 'Dallas', 'San Diego', 'Denver', 'Austin', 'San Jose', 'Orlando', 'Tampa', 'Las Vegas', 'Atlanta', 'Charlotte', 'Portland', 'Seattle', 'Boston', 'Philadelphia', 'Sacramento', 'Tucson', 'El Paso', 'Albuquerque', 'Nashville', 'Memphis', 'Baltimore', 'Milwaukee', 'Raleigh'];
const FALLBACK_PRODUCTS: ScannedProduct[] = [{
  title: 'El Anti-Manual de la IA',
  editionLabel: 'Blindaje Humano Completo',
  priceNumber: 7.97,
  priceText: '$7.97 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/cHem7FeV8hPwLg8VRhTGRT/ChatGPT_Image_17_abr_2026,_12_46_32.png'
}, {
  title: 'La Auditoría de los 10 Minutos',
  editionLabel: 'Order Bump',
  priceNumber: 7.77,
  priceText: '$7.77 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/2X2WG4gw7GPNcb7NPA1GSj/Order_bumps_1.png'
}, {
  title: 'Pack Cláusulas Anty-IA',
  editionLabel: 'Order Bump',
  priceNumber: 7.77,
  priceText: '$7.77 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/bWzSXZGGkbU54AP1pjDwGr/Order_Bumps_2.png'
}];
function scanPageProducts(): ScannedProduct[] {
  const products: ScannedProduct[] = [];
  const priceRegex = /\$\s?(\d+(?:[.,]\d{1,2})?)\s*(?:USD)?/gi;
  try {
    // Try JSON-LD first
    const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
    jsonLdScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '');
        if (data['@type'] === 'Product' && data.offers) {
          const offer = Array.isArray(data.offers) ? data.offers[0] : data.offers;
          products.push({
            title: data.name || 'Product',
            editionLabel: data.description?.substring(0, 30) || '',
            priceNumber: parseFloat(offer.price) || 0,
            priceText: `$${offer.price} ${offer.priceCurrency || 'USD'}`,
            thumbnailUrl: (Array.isArray(data.image) ? data.image[0] : data.image) || ''
          });
        }
      } catch {}
    });
    if (products.length > 0) return products;
    // Scan DOM for price-like elements
    const allText = document.body.innerText || '';
    const priceMatches = allText.match(priceRegex);
    if (priceMatches && priceMatches.length > 0) {
      // Find product sections by looking for images near prices
      const allImages = document.querySelectorAll('img[src*="magicpatterns"], img[src*="cdn"]');
      const seenTitles = new Set<string>();
      allImages.forEach(img => {
        const container = img.closest('section, [class*="card"], [class*="bump"], div');
        if (!container) return;
        const containerText = container.textContent || '';
        const containerPriceMatch = containerText.match(priceRegex);
        if (!containerPriceMatch) return;
        const priceStr = containerPriceMatch[0];
        const numMatch = priceStr.match(/(\d+(?:[.,]\d{1,2})?)/);
        const priceNum = numMatch ? parseFloat(numMatch[1].replace(',', '.')) : 0;
        // Find a title-like element (h1-h4 or bold text)
        const headings = container.querySelectorAll('h1, h2, h3, h4, strong, b');
        let title = '';
        headings.forEach(h => {
          const t = (h.textContent || '').trim();
          if (t.length > 3 && t.length < 80 && !t.match(priceRegex)) {
            if (!title) title = t;
          }
        });
        if (title && !seenTitles.has(title) && priceNum > 0) {
          seenTitles.add(title);
          products.push({
            title,
            editionLabel: '',
            priceNumber: priceNum,
            priceText: priceStr.trim(),
            thumbnailUrl: (img as HTMLImageElement).src
          });
        }
      });
    }
  } catch {}
  return products.length > 0 ? products : [];
}
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function getRandomInterval(): number {
  return Math.floor(Math.random() * 7000) + 8000; // 8-15 seconds
}
export function RecentPurchaseToast() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [visible, setVisible] = useState(false);
  const productsRef = useRef<ScannedProduct[]>([]);
  const nameHistoryRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const prefersReducedMotion = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  const pickName = useCallback(() => {
    const history = nameHistoryRef.current;
    const available = NAMES.filter(n => !history.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = pickRandom(pool);
    history.push(name);
    if (history.length > 10) history.shift();
    return name;
  }, []);
  const pickProduct = useCallback(() => {
    const catalog = productsRef.current;
    if (catalog.length > 0) {
      return pickRandom(catalog);
    }
    // Fallback distribution: 70% main product, 15% bump1, 15% bump2
    const roll = Math.random();
    if (roll < 0.7) return FALLBACK_PRODUCTS[0];
    if (roll < 0.85) return FALLBACK_PRODUCTS[1];
    return FALLBACK_PRODUCTS[2];
  }, []);
  const showNotification = useCallback(() => {
    const newNotif: Notification = {
      id: ++idRef.current,
      name: pickName(),
      city: pickRandom(CITIES),
      product: pickProduct()
    };
    setNotification(newNotif);
    setVisible(true);
    // Hide after 4 seconds
    setTimeout(() => {
      setVisible(false);
    }, 4000);
    // Schedule next
    timerRef.current = setTimeout(showNotification, getRandomInterval());
  }, [pickName, pickProduct]);
  useEffect(() => {
    // Scan products on mount
    const scanned = scanPageProducts();
    productsRef.current = scanned;
    // First notification after 3 seconds
    timerRef.current = setTimeout(showNotification, 3000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [showNotification]);
  return <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none" data-id="element-340">
      <AnimatePresence data-id="element-341">
        {visible && notification && <motion.div key={notification.id} initial={prefersReducedMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 40
      }} animate={prefersReducedMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={prefersReducedMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 40
      }} transition={{
        duration: prefersReducedMotion ? 0.15 : 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-dark-800 border border-gold-500/30 rounded-xl shadow-2xl shadow-black/40 px-4 py-3 flex items-center gap-3 max-w-sm w-max" data-id="element-342">
            <img src={notification.product.thumbnailUrl} alt={notification.product.title} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" data-id="element-343" />
            <div className="text-left min-w-0" data-id="element-344">
              <p className="text-white text-sm font-medium leading-tight" data-id="element-345">
                <span className="text-gold-400" data-id="element-346">{notification.name}</span> en{' '}
                {notification.city}
              </p>
              <p className="text-gray-400 text-xs mt-0.5 truncate" data-id="element-347">
                acaba de comprar{' '}
                <span className="text-gray-200" data-id="element-348">
                  {notification.product.title}
                </span>
                {notification.product.editionLabel && <span className="text-gray-500" data-id="element-349">
                    {' '}
                    — {notification.product.editionLabel}
                  </span>}
              </p>
              <p className="text-gold-500 text-xs font-semibold mt-0.5" data-id="element-350">
                {notification.product.priceText}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}